import { ChangeEvent, FormEvent, useCallback, useState } from "react"
import { toast } from "react-toastify"
import classNames from "classnames"

import { useChangeEnterprisePasswordMutation } from "store/api/enterpriseApi"

import getErrorMessage from "utils/getErrorMessage"

import { useAccountSettingsTabGuard } from "./navigationGuard"
import { AccountPageShell, Field, Footer } from "./shared"

import styles from "./index.module.scss"

const RESET_PASSWORD_FORM_ID = "enterprise-reset-password-form"

const passwordInitialState = {
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
}

export const ResetPasswordPage = () => {
  const [changeEnterprisePassword, { isLoading }] = useChangeEnterprisePasswordMutation()
  const [passwordValues, setPasswordValues] = useState(passwordInitialState)
  const isFormReady = Object.values(passwordValues).every(value => value.trim())
  const isDirty = Object.values(passwordValues).some(value => value.trim())

  const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setPasswordValues(prev => ({ ...prev, [name]: value }))
  }

  const savePassword = useCallback(async () => {
    if (!isFormReady) return false

    const result = await changeEnterprisePassword({
      current_password: passwordValues.currentPassword,
      password: passwordValues.newPassword,
      password_confirmation: passwordValues.confirmPassword,
    })
    const error = getErrorMessage(result.error)

    if (error) {
      toast.error(error)
      return false
    }

    toast.success("Password has been successfully updated!")
    setPasswordValues(passwordInitialState)
    return true
  }, [changeEnterprisePassword, isFormReady, passwordValues])

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    await savePassword()
  }

  useAccountSettingsTabGuard({
    isDirty,
    isSaving: isLoading,
    onSave: savePassword,
  })

  return (
    <AccountPageShell
      footer={
        <Footer
          form={RESET_PASSWORD_FORM_ID}
          isLoading={isLoading}
          isSaveDisabled={!isFormReady}
          saveButtonType="submit"
        />
      }
    >
      <form id={RESET_PASSWORD_FORM_ID} onSubmit={handleSubmit}>
        <h1 className={classNames(styles.title, styles.passwordTitle)}>Change Password</h1>
        <p className={styles.helperText}>
          The password should be at least 8 characters long, include upper and lower case characters, and contain at
          least one number.
        </p>
        <div className={classNames(styles.formGrid, styles.passwordForm)}>
          <Field
            className={styles.fieldFull}
            label="Current password*"
            name="currentPassword"
            value={passwordValues.currentPassword}
            placeholder="Current password"
            type="password"
            onChange={handlePasswordChange}
          />
          <Field
            className={styles.fieldFull}
            label="New password*"
            name="newPassword"
            value={passwordValues.newPassword}
            placeholder="New password"
            type="password"
            onChange={handlePasswordChange}
          />
          <Field
            className={styles.fieldFull}
            label="Confirm new password*"
            name="confirmPassword"
            value={passwordValues.confirmPassword}
            placeholder="Confirm new password"
            type="password"
            onChange={handlePasswordChange}
          />
        </div>
      </form>
    </AccountPageShell>
  )
}
